<script lang="ts" setup>
import type { MmReceivedQuotes } from '@forgd/contract/types'
import type { MarketMakerRequestStatusEnumType } from '@forgd/shared/enums'
import { formatValue } from '@forgd/formatter'
import { MarketMakerAvatar } from '@forgd/ui-kit/blocks/avatars'
import { KpiStat } from '@forgd/ui-kit/blocks/kpi-stat'
import { AvatarGroup } from '@forgd/ui-kit/avatar'
import { ChevronRightIcon } from '@heroicons/vue/24/outline'

const props = defineProps<{
  loading?: boolean
  projectId: string
  rfqStatus?: MarketMakerRequestStatusEnumType
  receivedQuotes?: MmReceivedQuotes
  engagements?: { id: string, imageUrl: string | null }[]
  lastReportedAt?: string | null
}>()

const showEngagements = computed(() => (props.engagements?.length ?? 0) > 0)
const isAwaiting = computed(() => props.lastReportedAt == null)

const rfqIcon = computed(() => {
  switch (props.rfqStatus) {
    case 'round_one_waiting_for_quotes':
    case 'round_one_quotes_in_review':
    case 'round_two_waiting_for_quotes':
    case 'round_two_quotes_in_review':
      return 'i-heroicons-outline-calendar-days'
    case 'round_one_quotes_accepted':
    case 'round_two_quotes_accepted':
      return 'i-heroicons-outline-check-circle'
    case 'cancelled':
      return 'i-heroicons-outline-x-circle'
    default:
      return 'i-lucide-zap'
  }
})

const rfqIconClass = computed(() => {
  switch (props.rfqStatus) {
    case 'round_one_quotes_accepted':
    case 'round_two_quotes_accepted':
      return 'text-accent-green'
    case 'cancelled':
      return 'text-error'
    default:
      return 'text-primal'
  }
})

const rfqText = computed(() => {
  switch (props.rfqStatus) {
    case 'round_one_waiting_for_quotes':
    case 'round_one_quotes_in_review':
      return 'In Round 1'
    case 'round_two_waiting_for_quotes':
    case 'round_two_quotes_in_review':
      return 'In Round 2'
    case 'round_one_quotes_accepted':
    case 'round_two_quotes_accepted':
      return 'Completed'
    case 'cancelled':
      return 'Cancelled'
    default:
      return 'Quick Start'
  }
})

const isQuickStart = computed(() => rfqText.value === 'Quick Start')

const rfqReceivedQuotes = computed(() => {
  if (!props.rfqStatus) {
    return 0
  }
  switch (props.rfqStatus) {
    case 'round_one_waiting_for_quotes':
    case 'round_one_quotes_in_review':
    case 'round_one_quotes_accepted':
      return props.receivedQuotes?.round1 ?? 0
    case 'round_two_waiting_for_quotes':
    case 'round_two_quotes_in_review':
    case 'round_two_quotes_accepted':
      return props.receivedQuotes?.round2 ?? 0
    case 'cancelled':
      return props.receivedQuotes?.round2 || props.receivedQuotes?.round1 || 0
    default:
      return 0
  }
})

const lastReportedLabel = computed(() => formatValue(props.lastReportedAt, 'date'))

function handleRfqAction() {
  // Quick Start (no RFQ yet) -> the EAMM request-for-quotations form
  let routePath = '/engage-a-market-maker/request-for-quotations'
  switch (props.rfqStatus) {
    case 'round_one_waiting_for_quotes':
    case 'round_one_quotes_in_review':
    case 'round_one_quotes_accepted':
      routePath = '/engage-a-market-maker/received-quotes'
      break
    case 'round_two_waiting_for_quotes':
    case 'round_two_quotes_in_review':
    case 'round_two_quotes_accepted':
      routePath = '/engage-a-market-maker/received-requotes'
      break
    case 'cancelled':
      routePath = '/engage-a-market-maker'
      break
  }
  navigateTo(`/project/${props.projectId}${routePath}`)
}

function handleEngagementsAction() {
  navigateTo(`/project/${props.projectId}/market-maker-monitoring`)
}
</script>

<template>
  <!-- Engagements view -->
  <KpiStat
    v-if="showEngagements"
    size="sm"
    title="Market Maker Engagements"
    :loading="loading"
    :value="isAwaiting ? 'Awaiting' : `${engagements?.length ?? 0} Active`"
    :description="isAwaiting ? 'Ready for first report' : 'Last Report'"
    :meta="isAwaiting ? undefined : lastReportedLabel"
    :action="{ icon: ChevronRightIcon, label: 'See Details' }"
    @action="handleEngagementsAction"
  >
    <template v-if="engagements?.length" #icon>
      <AvatarGroup>
        <MarketMakerAvatar v-for="engagement in engagements" :key="engagement.id" :src="engagement.imageUrl" size="xs" bordered />
      </AvatarGroup>
    </template>
  </KpiStat>

  <!-- RFQ status view -->
  <KpiStat
    v-else
    size="sm"
    title="Market Maker RFQ Status"
    tooltip="This card shows data related to the progression of any request for quote (&quot;RFQ&quot;) to market makers to support liquidity for your native token."
    :loading="loading"
    :value="rfqText"
    :description="isQuickStart ? 'Create your RFQ' : 'Total Received Quotes'"
    :meta="isQuickStart ? undefined : rfqReceivedQuotes"
    :action="{ icon: ChevronRightIcon, label: 'See Details' }"
    @action="handleRfqAction"
  >
    <template #icon>
      <i class="size-4" :class="[rfqIcon, rfqIconClass]" />
    </template>
  </KpiStat>
</template>
